/**
 * Service Wizard Store Slice
 *
 * GW-6344: Persists wizard draft state across navigation for the
 * /developer/services empty-state wizard (D7). Multi-step UX would lose
 * state if the user navigated away; this slice prevents that.
 *
 * Persisted via store.ts partialize config (see GW-6344).
 */

import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ServiceWizardSlice {
  /** Draft fields accumulated across wizard steps. null = wizard not started. */
  serviceWizardDraft: Record<string, unknown> | null;
  /** Current step index (0-based) */
  serviceWizardStep: number;
  /** True while the wizard is submitting to the backend */
  serviceWizardIsSubmitting: boolean;
  /** Submission error, null when clean */
  serviceWizardError: string | null;

  // Actions
  initServiceWizard: (initialDraft?: Record<string, unknown>) => void;
  updateServiceWizardDraft: (fields: Record<string, unknown>) => void;
  setServiceWizardStep: (step: number) => void;
  setServiceWizardIsSubmitting: (isSubmitting: boolean) => void;
  setServiceWizardError: (error: string | null) => void;
  resetServiceWizard: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  serviceWizardDraft: null,
  serviceWizardStep: 0,
  serviceWizardIsSubmitting: false,
  serviceWizardError: null,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createServiceWizardSlice: StateCreator<
  ServiceWizardSlice,
  [],
  [],
  ServiceWizardSlice
> = (set) => ({
  ...initialState,

  initServiceWizard: (initialDraft = {}) => {
    set({ serviceWizardDraft: initialDraft, serviceWizardStep: 0, serviceWizardError: null });
  },

  updateServiceWizardDraft: (fields) => {
    set((state) => ({
      serviceWizardDraft: {
        ...state.serviceWizardDraft,
        ...fields,
      },
    }));
  },

  setServiceWizardStep: (step) => {
    set({ serviceWizardStep: step });
  },

  setServiceWizardIsSubmitting: (isSubmitting) => {
    set({ serviceWizardIsSubmitting: isSubmitting });
  },

  setServiceWizardError: (error) => {
    set({ serviceWizardError: error });
  },

  resetServiceWizard: () => {
    set({ ...initialState });
  },
});
